import type { Feature } from 'geojson';

import { getKmlSvg } from '@atom/map';
import type { CollectionSymbology, RgbaColor } from '@atom/types/map';

import type { IconSize } from './kmlIconSizes';

/**
 * Base icon edge in pixels (at `scale` 1) for the round fallback marker and any
 * icon whose natural size couldn't be measured.
 */
const KML_ICON_SIZE = 32;
const DEFAULT_LINE_WIDTH = 3;
const DEFAULT_LINE_COLOR: RgbaColor = [80, 80, 80, 255];
const TRANSPARENT: RgbaColor = [0, 0, 0, 0];

const hexToRgb = (hex: string): [number, number, number] | null => {
  const match = /^#?([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i.exec(hex);
  return match
    ? [parseInt(match[1], 16), parseInt(match[2], 16), parseInt(match[3], 16)]
    : null;
};

const toRgba = (
  hex: unknown,
  opacity: unknown,
  fallback: RgbaColor,
): RgbaColor => {
  const rgb = typeof hex === 'string' ? hexToRgb(hex) : null;

  if (!rgb) {
    return fallback;
  }

  const alpha = typeof opacity === 'number' ? opacity : 1;
  return [rgb[0], rgb[1], rgb[2], Math.round(alpha * 255)];
};

/**
 * Convert one axis of a KML `<hotSpot>` (togeojson's `icon-offset` +
 * `icon-offset-units`) into a deck.gl anchor coordinate measured from the icon's
 * top-left, given the rendered axis length `size`. Fraction units scale to
 * `size`; `pixels`/`insetPixels` are taken as-is. `flip` converts KML's
 * bottom-origin Y to deck.gl's top-origin Y.
 */
const anchorAxis = (
  offset: number,
  unit: unknown,
  size: number,
  flip: boolean,
): number => {
  const fromStart =
    unit === 'pixels'
      ? offset
      : unit === 'insetPixels'
        ? size - offset
        : offset * size;

  return flip ? size - fromStart : fromStart;
};

/**
 * Resolve a placemark's icon anchor from its `<hotSpot>` (X against `width`, Y
 * against `height`), or `null` when it declares none so the caller can center it.
 */
const iconAnchor = (
  feature: Feature,
  width: number,
  height: number,
): { anchorX: number; anchorY: number } | null => {
  const offset = feature.properties?.['icon-offset'];
  const units = feature.properties?.['icon-offset-units'];

  if (!Array.isArray(offset) || offset.length < 2) {
    return null;
  }

  const [xUnit, yUnit] = Array.isArray(units) ? units : [];

  return {
    anchorX: anchorAxis(offset[0], xUnit, width, false),
    anchorY: anchorAxis(offset[1], yUnit, height, true),
  };
};

/**
 * Build a `CollectionSymbology` that renders each KML feature from its own
 * embedded style. `iconSizes` maps an icon URL to its measured natural
 * dimensions (see `measureIconSizes`); unmeasured icons use `KML_ICON_SIZE`.
 */
export const kmlSymbology = (
  markerId: number,
  iconSizes?: Map<string, IconSize>,
): CollectionSymbology => {
  const fallbackIconUrl = getKmlSvg(markerId);

  return {
    getIcon: (feature: Feature) => {
      const href = feature.properties?.icon;
      const hasOwnIcon = typeof href === 'string' && Boolean(href);
      const url = hasOwnIcon ? href : fallbackIconUrl;

      if (!url) {
        return null;
      }

      // Honor `<IconStyle><scale>` (togeojson's `icon-scale`), default 1.
      const scale = feature.properties?.['icon-scale'];
      const scaleFactor = typeof scale === 'number' && scale > 0 ? scale : 1;

      // Draw an icon at its natural dimensions (preserving aspect ratio) times `scale`.
      const natural = hasOwnIcon ? iconSizes?.get(url) : undefined;
      const width = (natural?.width ?? KML_ICON_SIZE) * scaleFactor;
      const height = (natural?.height ?? KML_ICON_SIZE) * scaleFactor;

      // Anchor an icon at its `<hotSpot>`, defaulting to the bottom tip.
      // The fallback marker stays centered (no anchor).
      const anchor = hasOwnIcon
        ? (iconAnchor(feature, width, height) ?? {
            anchorX: width / 2,
            anchorY: height,
          })
        : {};

      return {
        url,
        width,
        height,
        ...anchor,
      };
    },
    getFillColor: (feature: Feature) =>
      toRgba(
        feature.properties?.fill,
        feature.properties?.['fill-opacity'],
        TRANSPARENT,
      ),
    getLineColor: (feature: Feature) =>
      toRgba(
        feature.properties?.stroke,
        feature.properties?.['stroke-opacity'],
        DEFAULT_LINE_COLOR,
      ),
    getLineWidth: (feature: Feature) => {
      const width = feature.properties?.['stroke-width'];
      return typeof width === 'number' && width > 0
        ? width
        : DEFAULT_LINE_WIDTH;
    },
  };
};
